import React, { useEffect, useMemo, useState } from 'react';
import { rome as moment } from '../../../../utils/date';
import Modal from '../../../../shared/modal';
import { useAppDispatch, useAppSelector } from '../../../../app/hooks';
import {
  tryToListFeedbacksByTrip,
  tryToUpdateFeedbackDescription,
  tryToDeleteFeedback,
  tryToCreateFeedback,
} from '../../../../store/admin/trips/feedbacksSlice';
import { AdminFeedbacksApi } from '../../../../store/admin/trips/adminFeedbacksApi';
import { buildFeedbacksCsv, downloadCsv } from '../../../../utils/csv/exportFeedbacksCsv';
import CarriageCabinSelect from '../components/CarriageCabinSelect';
import { ReportsService } from '../../../../store/admin/reports/reportsApi';

type FeedbackItem = {
  _id: string;
  description?: string;
  type?: 'staff' | 'guest';
  action?: boolean;
  actionStatus?: 'allocated_and_acknowledged' | 'allocated_but_not_acknowledged' | 'unallocated';
  createdAt?: string;
  additionalInfo?: {
    sentiment?: 'positive' | 'negative' | 'neutral';
    tripId?: string;
    description?: string;
  };
};

type PageInfo = {
  totalItems: number;
  page: number;
  size: number;
  totalPages: number;
};

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 20, 50];

const TripDetailsModal = ({
  open,
  onClose,
  data,
  cabins,
  onDataChanged,
}: {
  open: boolean;
  data?: any;
  cabins?: any[];
  onClose: () => void;
  onDataChanged?: () => void;
}) => {
  const dispatch = useAppDispatch();
  const [trip] = useState<any>(data);

  const [page, setPage] = useState<number>(1);
  const [size, setSize] = useState<number>(10);

  // EDIT / DELETE
  const [editOpen, setEditOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<FeedbackItem | null>(null);
  const [editText, setEditText] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<FeedbackItem | null>(null);
  const [working, setWorking] = useState(false);

  // CREATE
  const [createOpen, setCreateOpen] = useState(false);
  const [createWorking, setCreateWorking] = useState(false);
  const [createText, setCreateText] = useState('');
  const [createType, setCreateType] = useState<'staff' | 'guest'>('staff');
  const [createSentiment, setCreateSentiment] = useState<'positive' | 'negative' | 'neutral'>('neutral');
  const [createCabinId, setCreateCabinId] = useState<string>(''); // e.g. 'A6'

  const tripId = useMemo(
    () => trip?.additionalInfo?.tripId || trip?._id || trip?.id || trip?.tripId,
    [trip]
  );

  const bucket = useAppSelector((s) => (tripId ? s.tripFeedbacks.byTrip[tripId] : undefined));
  const isLoading = useAppSelector((s) => s.tripFeedbacks.isLoading);
  const error = useAppSelector((s) => s.tripFeedbacks.error);
  const feedbacks: FeedbackItem[] = bucket?.items || [];
  const pageInfo: PageInfo = bucket?.pageInfo || { totalItems: 0, page: 1, size: 10, totalPages: 0 };

  // fetch per open/page/size
  useEffect(() => {
    if (!open || !tripId) return;
    dispatch(tryToListFeedbacksByTrip({ tripId, page, size }));
  }, [open, tripId, page, size, dispatch]);

  // EDIT flow
  const openEdit = (fb: FeedbackItem) => {
    setEditTarget(fb);
    setEditText(fb?.additionalInfo?.description ?? fb?.description ?? '');
    setEditOpen(true);
  };

  const saveEdit = async () => {
    if (!editTarget?._id || !editText.trim() || !tripId) return;
    setWorking(true);
    try {
      // 1) Update description on the backend
      await dispatch(
        tryToUpdateFeedbackDescription({
          id: editTarget._id,
          description: editText.trim(),
        })
      ).unwrap();

      // 2) Re-fetch the list so all fields (incl. sentimentScore, etc.) are fresh
      await dispatch(
        tryToListFeedbacksByTrip({
          tripId,
          page,
          size,
        })
      ).unwrap();

      // 3) Close modal / cleanup
      setEditOpen(false);
      setEditTarget(null);
      onDataChanged?.();
    } catch (e: any) {
      alert(e?.message || 'Failed to update description');
    } finally {
      setWorking(false);
    }
  };


  // DELETE flow
  const openDelete = (fb: FeedbackItem) => {
    setDeleteTarget(fb);
    setDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget?._id) return;
    setWorking(true);
    try {
      await dispatch(tryToDeleteFeedback({ id: deleteTarget._id })).unwrap();
      setDeleteOpen(false);
      setDeleteTarget(null);
      onDataChanged?.();

      // if page emptied and not first, go back a page
      const itemsLeft = (bucket?.items?.length ?? 1) - 1;
      if (itemsLeft <= 0 && page > 1) {
        setPage((p) => Math.max(1, p - 1));
      }
    } catch (e: any) {
      alert(e?.message || 'Failed to delete feedback');
    } finally {
      setWorking(false);
    }
  };

  // CREATE flow
  const openCreate = () => {
    setCreateText('');
    setCreateType('staff');
    setCreateSentiment('neutral');
    setCreateCabinId('');
    setCreateOpen(true);
  };

  const saveCreate = async () => {
    if (!tripId || !createText.trim()) return;
    setCreateWorking(true);
    try {
      await dispatch(
        tryToCreateFeedback({
          tripId,
          payload: {
            description: createText.trim(),
            type: createType,
            sentiment: createSentiment,
            additionalInfo: {
              itineraryId: trip?.itinerary?._id || trip?.itineraryId,
              startDate: trip?.startDate,
              endDate: trip?.endDate,
              trainManagerId: trip?.trainManager?._id || trip?.trainManagerId,
              ...(createCabinId ? { cabinId: createCabinId } : {}),
            },
          },
        })
      ).unwrap();

      setCreateOpen(false);
      setCreateText('');
      onDataChanged?.();
      // If on later pages, jump to page 1 so the new item is visible
      if (page > 1) setPage(1);
    } catch (e: any) {
      alert(e?.message || 'Failed to create feedback');
    } finally {
      setCreateWorking(false);
    }
  };

  // Export CSV
  async function handleExportTripFeedbacks(id?: string) {
    if (!id) return;
    const { data } = await AdminFeedbacksApi.exportAllForTrip(id);
    const rows = data?.data || [];
    const csv = buildFeedbacksCsv(rows);
    downloadCsv(csv, `trip-${id}-feedbacks.csv`);
  }

  // Download Train Manager Report
  const handleDownloadTrainManagerReport = async () => {
    if (!tripId) return;
    try {
      const response = await ReportsService.tryToDownloadTrainManagerReport(tripId);
      // Handle response - API returns { pdfUrl: "https://..." }
      if (response?.data?.pdfUrl) {
        const pdfUrl = response.data.pdfUrl;

        // Fetch the PDF as a blob to force download
        try {
          const pdfResponse = await fetch(pdfUrl);
          const blob = await pdfResponse.blob();
          const blobUrl = URL.createObjectURL(blob);

          // Create a temporary anchor element to trigger download
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = `train-manager-report-${tripId}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          // Clean up the blob URL
          setTimeout(() => URL.revokeObjectURL(blobUrl), 100);
        } catch (fetchError) {
          // If fetch fails (CORS issue), fall back to opening the URL
          window.open(pdfUrl, '_blank');
        }
      } else {
        alert('Report URL not found in response');
      }
    } catch (error) {
      console.error('Failed to download train manager report:', error);
      alert('Failed to download report. Please try again.');
    }
  };

  const canPrev = page > 1;
  const canNext = pageInfo?.totalPages ? page < pageInfo.totalPages : false;

  return (
    <>
      <Modal
        open={open}
        title={'Trip Details'}
        onClose={onClose}
        contentContainerStyle='!rounded-xl min-w-[95%] md:min-w-[90%] lg:min-w-[80%]'
      >
        <div className='p-2 max-w-[95vw] md:min-w-[70%] lg:min-w-[60%] min-h-[500px]'>
          <div className='flex flex-col gap-4 p-4 dark:text-primary-darkMode'>
            {/* Trip header */}
            <div>
              <p className='font-bold dark:text-primary-darkMode'>Itinerary</p>
              <p>{trip?.itinerary?.journeyTitle} {trip?.itinerary?.duration}</p>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <p className='font-bold dark:text-primary-darkMode'>Start Date</p>
                <p>{moment(trip?.startDate).format('DD/MM/YYYY, hh:mm A')}</p>
              </div>
              <div>
                <p className='font-bold dark:text-primary-darkMode'>End Date</p>
                <p>{moment(trip?.endDate).format('DD/MM/YYYY, hh:mm A')}</p>
              </div>
            </div>

            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              <div>
                <p className='font-bold dark:text-primary-darkMode'>Train Manager</p>
                <p>{trip?.trainManager?.name}</p>
              </div>
              <div>
                <p className='font-bold dark:text-primary-darkMode'>Train</p>
                <p>{trip?.train?.name}</p>
              </div>
            </div>

            {/* Download Train Manager Report Button */}
            <div className='flex justify-start'>
              <button
                className='px-4 py-2 rounded-lg border bg-blue-600 text-white hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 disabled:opacity-50 disabled:cursor-not-allowed'
                onClick={handleDownloadTrainManagerReport}
                disabled={!tripId}
              >
                Download Train Managers Report
              </button>
            </div>

            {/* Crew table */}
            <div className='border rounded p-3'>
              <div className='font-bold mb-2'>Crew Members</div>
              <table className='min-w-full bg-white dark:bg-black dark:text-primary-darkMode'>
                <thead className='bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-primary-darkMode rounded-xl'>
                  <tr>
                    <th className='px-4 py-3'>Name</th>
                    <th className='px-4 py-3'>Role</th>
                    <th className='px-4 py-3'>Cabin</th>
                  </tr>
                </thead>
                <tbody>
                  {trip?.assignedCabins?.length > 0 &&
                    trip.assignedCabins.map((row: any, idx: number) => {
                      const findCrew =
                        row?.crewMemberId &&
                        data?.crewMembers?.length > 0 &&
                        data?.crewMembers?.find((item: any) => item?._id === row?.crewMemberId);

                      return (
                        <tr key={idx} className='hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm'>
                          <td className='px-4 py-4'>{findCrew?.name} {findCrew?.lastName}</td>
                          <td className='px-4 py-4'>{row?.role}</td>
                          <td className='px-4 py-4'>
                            {row?.cabinIds?.length > 0 &&
                              row.cabinIds
                                .map((id: string) => {
                                  const cabin = cabins?.find((c: any) => c?.cabinId === id);
                                  return cabin ? `${cabin.type} ${cabin.cabinId}` : id;
                                })
                                .join(', ')}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>

            {/* Feedbacks table */}
            <div className='border rounded p-3'>
              <div className='flex items-center justify-between mb-3 gap-3'>
                <div className='font-bold'>Feedbacks</div>

                <div className='flex items-center gap-2'>
                  <button
                    className='px-3 py-1 rounded-lg border hover:bg-gray-100 dark:hover:bg-zinc-900'
                    onClick={openCreate}
                  >
                    Add Feedback
                  </button>

                  <button
                    className='px-3 py-1 rounded-lg border hover:bg-gray-100 dark:hover:bg-zinc-900'
                    onClick={() => handleExportTripFeedbacks(tripId)}
                  >
                    Export CSV
                  </button>

                  <span className='text-sm opacity-80'>Rows per page</span>
                  <select
                    value={size}
                    onChange={(e) => { setPage(1); setSize(Number(e.target.value)); }}
                    className='border rounded px-2 py-1 bg-white dark:bg-black'
                  >
                    {DEFAULT_PAGE_SIZE_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className='overflow-x-auto'>
                <table className='min-w-full bg-white dark:bg-black dark:text-primary-darkMode'>
                  <thead className='bg-white dark:bg-black text-left text-sm font-medium text-black dark:text-primary-darkMode'>
                    <tr>
                      <th className='px-4 py-3'>Date</th>
                      <th className='px-4 py-3'>Type</th>
                      <th className='px-4 py-3'>Sentiment</th>
                      <th className='px-4 py-3'>Description</th>
                      <th className='px-4 py-3'>Action Status</th>
                      <th className='px-4 py-3 text-right'>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && (
                      <tr>
                        <td colSpan={6} className='px-4 py-6 text-center text-sm opacity-70'>Loading…</td>
                      </tr>
                    )}

                    {!isLoading && error && (
                      <tr>
                        <td colSpan={6} className='px-4 py-6 text-center text-sm text-red-500'>{error}</td>
                      </tr>
                    )}

                    {!isLoading && !error && feedbacks.length === 0 && (
                      <tr>
                        <td colSpan={6} className='px-4 py-6 text-center text-sm opacity-70'>No feedbacks found for this trip.</td>
                      </tr>
                    )}

                    {!isLoading && !error && feedbacks.map((fb) => {
                      const desc = fb?.additionalInfo?.description ?? fb?.description;
                      return (
                        <tr key={fb._id} className='hover:bg-gray-50 dark:hover:bg-bgColor-darkMode text-sm'>
                          <td className='px-4 py-3 whitespace-nowrap'>
                            {fb?.createdAt ? moment(fb.createdAt).format('DD/MM/YYYY, hh:mm A') : '—'}
                          </td>
                          <td className='px-4 py-3 capitalize'>{fb?.type || '—'}</td>
                          <td className='px-4 py-3 capitalize'>{fb?.additionalInfo?.sentiment || '—'}</td>
                          <td className='px-4 py-3 max-w-[420px] truncate' title={desc}>{desc || '—'}</td>
                          <td className='px-4 py-3 capitalize'>{fb?.actionStatus || '—'}</td>
                          <td className='px-4 py-3'>
                            <div className='flex items-center justify-end gap-2'>
                              <button
                                className='px-3 py-1 rounded-lg border hover:bg-gray-100 dark:hover:bg-zinc-900'
                                onClick={() => openEdit(fb)}
                              >
                                Edit
                              </button>
                              <button
                                className='px-3 py-1 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10'
                                onClick={() => openDelete(fb)}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className='flex items-center justify-between mt-4'>
                <div className='text-sm opacity-80'>
                  {pageInfo?.totalItems ?? 0} items • Page {pageInfo?.page ?? page} of {pageInfo?.totalPages ?? 0}
                </div>
                <div className='flex items-center gap-2'>
                  <button
                    className='px-3 py-1 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-zinc-900'
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={!canPrev}
                  >
                    Prev
                  </button>
                  <button
                    className='px-3 py-1 rounded-lg border disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100 dark:hoverbg-zinc-900'
                    onClick={() => setPage(p => p + 1)}
                    disabled={!canNext}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </Modal>

      {/* Create modal */}
      <Modal
        open={createOpen}
        title='Add Feedback'
        onClose={() => !createWorking && setCreateOpen(false)}
        contentContainerStyle='!rounded-xl min-w-[90%] md:min-w-[600px]'
      >
        <div className='p-4 flex flex-col gap-3'>
          <label className='text-sm opacity-80 dark:text-white'>Description</label>
          <textarea
            className='border rounded p-2 min-h-[120px] bg-white dark:bg-black dark:text-white text-sm'
            value={createText}
            onChange={(e) => setCreateText(e.target.value)}
            disabled={createWorking}
          />

          <div className='grid grid-cols-1 md:grid-cols-2 gap-3'>
            <div>
              <label className='text-sm opacity-80 dark:text-white'>Type</label>
              <select
                className='border mt-1 rounded px-2 py-1 w-full bg-white dark:bg-black dark:text-white'
                value={createType}
                onChange={(e) => setCreateType(e.target.value as 'staff' | 'guest')}
                disabled={createWorking}
              >
                <option value='staff'>Staff</option>
                <option value='guest'>Guest</option>
              </select>
            </div>

            <CarriageCabinSelect
              value={createCabinId}
              onChange={(value) => setCreateCabinId(value)}
            />
          </div>

          <div className='flex items-center justify-end gap-2'>
            <button
              className='px-3 py-1 rounded-lg border hover:bg-gray-100 dark:hover:bg-zinc-900 text-sm dark:text-white'
              onClick={() => setCreateOpen(false)}
              disabled={createWorking}
            >
              Cancel
            </button>
            <button
              className='px-3 py-1 rounded-lg border border-green-300 text-green-700 hover:bg-green-50 dark:hover:bg-green-900/10 disabled:opacity-50 disabled:cursor-not-allowed'
              onClick={saveCreate}
              disabled={createWorking || !createText.trim()}
            >
              {createWorking ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Edit modal */}
      <Modal
        open={editOpen}
        title="Edit Feedback"
        onClose={() => !working && setEditOpen(false)}
        contentContainerStyle="!rounded-xl min-w-[90%] md:min-w-[600px]"
      >
        <div className="p-4 flex flex-col gap-3">
          <label className="text-sm opacity-80 dark:text-white">New description</label>
          <textarea
            className="border rounded p-2 min-h-[120px] bg-white dark:bg-black dark:text-white"
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            disabled={working}
          />
          <div className="flex items-center justify-end gap-2">
            <button className="px-3 py-1 rounded-lg border hover:bg-gray-100 dark:hover:bg-zinc-900 dark:text-white text-sm" onClick={() => setEditOpen(false)} disabled={working}>Cancel</button>
            <button className="px-3 py-1 rounded-lg border border-green-300 text-green-700 hover:bg-green-50 dark:hover:bg-green-900/10 text-sm" onClick={saveEdit} disabled={working || !editText.trim()}>
              {working ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Delete confirm modal */}
      <Modal
        open={deleteOpen}
        title="Delete Feedback"
        onClose={() => !working && setDeleteOpen(false)}
        contentContainerStyle="!rounded-xl min-w-[90%] md:min-w-[480px]"
      >
        <div className="p-4">
          <p className="mb-6">Are you sure you want to delete this feedback?</p>
          <div className="flex items-center justify-end gap-2">
            <button className="px-3 py-1 rounded-lg border hover:bg-gray-100 dark:hover:bg-zinc-900" onClick={() => setDeleteOpen(false)} disabled={working}>Cancel</button>
            <button className="px-3 py-1 rounded-lg border border-red-300 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10" onClick={confirmDelete} disabled={working}>
              {working ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default TripDetailsModal;
